import { IFormJBPHPContactRoles } from 'legacy/lut/classes/models/partner360/IFormHPContacts'
import { useMemo, useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Dropdown } from 'core-components/library/dropdown'
import { Loader, LoaderOverlay } from 'core-components/library/loader'
import { Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'
import { Card } from 'partner360/components/card'

import { CockpitCardHeader } from '../../../components/cockpitCardHeader'
import { CockpitIconButton } from '../../../components/cockpitIconButton'
import { ICockpitSalesTeamContact } from '../../helpers/buildSalesTeamItems'
import {
  companySalesTeamTranslation as KEYS,
  salesTeamRequestTranslation as REQUEST_KEYS,
} from '../../translation'
import {
  ISalesTeamContactAction,
  SalesTeamContactActions,
} from '../salesTeamContactActions/SalesTeamContactActions'
import { SalesTeamRequestStatus } from '../salesTeamRequestStatus/SalesTeamRequestStatus'

import styles from './SalesTeamExpandedView.module.scss'

type Contact = ICockpitSalesTeamContact
interface Props {
  items: Contact[]
  loading: boolean
  requestsLoading?: boolean
  error: boolean
  requestsError?: boolean
  saveError?: boolean
  refresh: () => void
  getContactMenuItems: (item: Contact) => ISalesTeamContactAction[]
  busy: boolean
  canOpenRequest?: (item: Contact) => boolean
  onOpenRequest: (item: Contact) => void
  role: string
  onRoleChange: (role: string) => void
  partnerRoles: IFormJBPHPContactRoles[]
}

export const SalesTeamExpandedView = ({
  items,
  loading,
  requestsLoading = false,
  error,
  requestsError,
  saveError,
  refresh,
  getContactMenuItems,
  busy,
  onOpenRequest,
  canOpenRequest = () => true,
  role,
  onRoleChange,
  partnerRoles,
}: Props) => {
  const t = useSafetyTranslation()
  const updating = busy || requestsLoading
  const allRoles = t(REQUEST_KEYS.allRoles)
  const [page, setPage] = useState(0)
  const roles = useMemo(
    () =>
      new Crud({
        fetcher: new Static([
          { id: '', caption: allRoles },
          ...Array.from(
            new Set(
              [
                ...items.map((item) => item.Roles),
                ...partnerRoles.map((item) => item.Roles),
              ].filter(Boolean)
            )
          )
            .sort()
            .map((value) => ({ id: value, caption: value })),
        ]),
        keyField: 'id',
        defaultPaging: { page: 0, pageSize: 100 },
      }),
    [items, partnerRoles, allRoles]
  )
  const filtered = items.filter((item) => !role || item.Roles?.includes(role))
  const people = filtered.filter(
    (item) => !item.requestOnly && !(item.TicketId > 0 && item.RequestAction === 'Add')
  )
  const additions = filtered.filter(
    (item) => item.requestOnly || (item.TicketId > 0 && item.RequestAction === 'Add')
  )
  const currentPage = Math.min(page, Math.max(0, Math.ceil(people.length / 5) - 1))
  const start = currentPage * 5
  const renderContact = (item: Contact) => (
    <Card
      key={`${item.EntryId}-${item.TicketId}-${item.Roles}`}
      hoverable={false}
      className={styles.expandedContact}
      header={
        <CockpitCardHeader
          title={
            <>
              {[item.FirstName, item.LastName].filter(Boolean).join(' ') || t(REQUEST_KEYS.noValue)}{' '}
              {!!item.LeadPBM && (
                <span className={styles.lead}>
                  <i className="kbase kbase-star-alt" aria-hidden />{' '}
                  <Translation text={KEYS.leadPBM} />
                </span>
              )}
            </>
          }
          description={item.Email || t(REQUEST_KEYS.noValue)}
          actions={
            <SalesTeamContactActions
              email={item.Email}
              menuItems={getContactMenuItems(item)}
              disabled={busy}
            />
          }
        />
      }
    >
      <dl className={styles.expandedFacts}>
        <div>
          <dt>
            <Translation text={KEYS.role} />
          </dt>
          <dd>{item.Roles || t(REQUEST_KEYS.noValue)}</dd>
        </div>
        <div>
          <dt>
            <Translation text={KEYS.bUnit} />
          </dt>
          <dd>{item.BusinessUnit?.split('/').join(', ') || t(REQUEST_KEYS.noValue)}</dd>
        </div>
        <div>
          <dt>
            <Translation text={KEYS.assignmentPeriod} />
          </dt>
          <dd>{item.AssignmentPeriod || t(REQUEST_KEYS.noValue)}</dd>
        </div>
      </dl>
      <SalesTeamRequestStatus
        item={item}
        busy={busy}
        actionable={canOpenRequest(item)}
        onClick={() => onOpenRequest(item)}
      />
    </Card>
  )

  return (
    <div className={styles.expandedBody} aria-busy={updating}>
      {updating && !loading && (
        <div role="status" aria-label={t(busy ? REQUEST_KEYS.saving : REQUEST_KEYS.loading)}>
          <LoaderOverlay show dimension={{ width: 32, height: 32 }} />
        </div>
      )}
      <div className={styles.expandedContent} inert={updating || undefined}>
        {saveError && (
          <div role="alert">
            <Translation text={REQUEST_KEYS.saveError} />
          </div>
        )}
        {requestsError && (
          <div className={styles.state} role="alert">
            <Translation text={REQUEST_KEYS.requestsLoadError} />
            <CockpitIconButton
              icon="kbase kbase-reload"
              label={t(REQUEST_KEYS.retry)}
              showLabel
              onClick={refresh}
            />
          </div>
        )}
        {loading ? (
          <Loader dimension={{ width: 32, height: 32 }} />
        ) : error ? (
          <div className={styles.state}>
            <Translation text={REQUEST_KEYS.loadError} />
            <CockpitIconButton
              icon="kbase kbase-reload"
              label={t(REQUEST_KEYS.retry)}
              showLabel
              onClick={refresh}
            />
          </div>
        ) : (
          <>
            <div className={styles.expandedToolbar}>
              <div className={styles.roleFilter}>
                <Dropdown
                  source={roles}
                  caption="caption"
                  label={t(REQUEST_KEYS.filterRole)}
                  value={role}
                  onChange={(value: string) => {
                    onRoleChange(value)
                    setPage(0)
                  }}
                />
              </div>
            </div>
            {people.slice(start, start + 5).map(renderContact)}
            {!people.length && (
              <p>
                <Translation text={role ? REQUEST_KEYS.emptyRole : KEYS.emptySalesTeamContent} />
              </p>
            )}
            {people.length > 5 && (
              <div className={styles.navigation}>
                <CockpitIconButton
                  icon="kbase kbase-chevron-left"
                  label={t(REQUEST_KEYS.previousPage)}
                  disabled={currentPage === 0}
                  onClick={() => setPage(currentPage - 1)}
                />
                <span>
                  <Translation text={REQUEST_KEYS.page} />{' '}
                  <span className={styles.figure}>{currentPage + 1}</span> {t(REQUEST_KEYS.of)}{' '}
                  <span className={styles.figure}>{Math.ceil(people.length / 5)}</span>
                </span>
                <CockpitIconButton
                  icon="kbase kbase-chevron-right"
                  label={t(REQUEST_KEYS.nextPage)}
                  disabled={start + 5 >= people.length}
                  onClick={() => setPage(currentPage + 1)}
                />
              </div>
            )}
            {additions.map(renderContact)}
          </>
        )}
      </div>
    </div>
  )
}
